
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

import { DateTime, Duration } from "luxon"

// ---- Types ----
import { Field, Team, DayOfWeek } from "@/types"
import { GroupedUserSchedules, TotalType, UserSchedule, TotalsRow, TimeManagementSettings } from "@/types/timeManagement"
import { LabeledValueWithFieldInfo, UserWithFields, CurrentUser, ExtendedPermissions } from "@/types/user"
import { IntegrationUploadOptions, IntegrationUploadOptionsByIntegration } from "@/types/integration"
import { TenantFeatures } from "@/types/tenantFeatures"
import { IdLabelPair } from "@/types/labeledValue"

// ---- Components ----
import vSelect from "vue-select"
// @ts-ignore
import VDatePicker from "v-calendar/lib/components/date-picker.umd"
import TimeCalendarUserRows from "./TimeCalendar/TimeCalendarUserRows.vue"
import IntegrationFileUpload from "@/components/Integrations/IntegrationFileUpload.vue"
import SkeletonLoader from "../SkeletonLoader.vue"
import UserImage from "@/components/UserImage.vue"
import FieldControl from "../Fields/FieldControl.vue"

// ---- Services ----
import api from "@/services/api"

// ---- Utilities ----
import cloneDeep from "lodash/cloneDeep"
import { getEndDateOfWeek, getWeekNumber } from "@/utils/dateUtils"

const setup = namespace("setup")

interface Week {
	weekNumber: number,
	daySpan: number
}

interface DateColumn {
	label: string,
	key: string,
	dateFullValue:string,
	week: number,
	isWeekend: boolean
}

enum DefaultRangeOptions {
	RestOfMonth = "restOfMonth",
	Next3Weeks = "next3Weeks",
	Next4Weeks = "next4Weeks"
}

enum TimeShiftDirection {
	Backwards = "backwards",
	Forwards = "forwards"
}

@Component({
	components: {
		"skeleton": SkeletonLoader,
		"v-select": vSelect,
		"user-image": UserImage,
		"date-picker": VDatePicker,
		"schedule-group": TimeCalendarUserRows,
		"file-upload": IntegrationFileUpload,
		"field-control": FieldControl
	}
})
export default class TimeCalendar extends Vue {
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@State("me") currentUser!: CurrentUser | null
	@State teams!: Team[]
	@State timeManagementSettings!: TimeManagementSettings | null
	@State fileUploadOptions!: IntegrationUploadOptions[] | null

	@Action loadFileUploadOptions!: () => Promise<void>
	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter getDefaultFirstDayOfWeek!: DayOfWeek
	@Getter getIsUserInitComplete!: boolean

	@setup.State userFields!: Field[]
	@setup.Action loadUserFieldsIfUnloaded!: () => Promise<void>

	// Constants

	private hoursPerDay = 9

	private defaultRangeOptions = [
		{ value: DefaultRangeOptions.RestOfMonth, label: "Rest of current month" },
		{ value: DefaultRangeOptions.Next3Weeks, label: "Next 3 weeks" },
		{ value: DefaultRangeOptions.Next4Weeks, label: "Next 4 weeks" }
	]

	private TimeShiftStepLabel = "1 Week"

	TimeShiftDirection = TimeShiftDirection
	private TenantFeatures = TenantFeatures

	// Data

	private groupedUserSchedules: GroupedUserSchedules[] = []

	users: UserWithFields[] | null = null

	showingFilters: boolean = false
	showingUpload: boolean = false
	loadingCases: boolean = false
	errorLoading: boolean = false
	private awaitingUserInit: boolean = false

	groupLabels: { [key: string]: string } = {}

	shiftTitles: string[] = []
	shiftColors: Record<string, string> = {}

	searchRange: { start: Date, end: Date } = {
		start: new Date(Date.now()),
		end: new Date(Date.now())
	}

	private userDefinitionFieldFilters: { [definitionFieldId: string]: {
		enabled: boolean,
		values: IdLabelPair[]
	}} = {}

	/**
	 * Gets list of dates within given date range
	 * @returns {DateTime[]} list of luxon datetimes
	 */
	get datesInRange (): DateTime[] {
		const start = DateTime.fromJSDate(this.searchRange.start, { zone: "utc" })
		const end = DateTime.fromJSDate(this.searchRange.end, { zone: "utc" })

		const duration = end.diff(start).shiftTo("days")

		// Generate the list of dates
		return Array.from(
			{ length: duration.days + 1 }, // Number of days to interate over
			(_, i) => start.plus(Duration.fromObject({ days: i })) // Each time, add a day
		)
	}

	/**
	 * Uses datesInRange to load list of date columns
	 * @returns {DateColumn[]} Date column list
	 */
	get dateColumns (): DateColumn[] {
		return this.datesInRange.map(d => ({
			label: d.weekdayShort + " | " + d.toLocaleString(),
			key: d.toISODate(),
			dateFullValue: d.toISO(),
			week: getWeekNumber(d, this.firstDayOfWeek),
			isWeekend: d.weekday >= 6
		}))
	}

	/**
	 * Group dateColumns to make week columns
	 * @returns {Week[]} list of weeks
	 */
	get weeks (): Week[] {
		return Object.values(this.dateColumns.groupBy("week"))
			.sort((weekA, weekB) => {
				if (weekA.length === 0 || weekB.length === 0) return -1
				if (new Date(weekA[0].dateFullValue).getTime() < new Date(weekB[0].dateFullValue).getTime()) return -1

				return weekA[0].week < weekB[0].week ? -1 : 1
			}).map(week => ({
				weekNumber: week[0].week,
				daySpan: week.length
			}))
	}

	get timeUploadOptions (): IntegrationUploadOptionsByIntegration[] {
		if (!this.fileUploadOptions) return []
		return this.fileUploadOptions.map(
			i => (
				i.uploadOptions
					.filter(o => o.purpose === 0)
					.map(o => ({
						integrationKey: i.integrationKey,
						integrationTitle: i.integrationTitle,
						uploadKey: o.key,
						uploadTitle: o.title,
						extensions: o.allowedFileExtensions
					})
					)
			)
		).flat()
	}

	get columnCount () {
		return this.datesInRange.length + 1
	}

	private get firstDayOfWeek () {
		return this.getIsFeatureEnabled(TenantFeatures.ScheduleManagementUseTenancyStartDayOfWeek)
			? this.getDefaultFirstDayOfWeek
			: DayOfWeek.Monday
	}

	get groupBy (): string | null {
		return this.value ? this.value.groupBy : null
	}
	set groupBy (value: string | null) {
		this.$emit("input", {
			...this.value,
			groupBy: value
		})
	}

	get hideUngroupedUsers (): boolean {
		return this.value ? this.value.hideUngroupedUsers : true
	}
	set hideUngroupedUsers (value: boolean) {
		this.$emit("input", {
			...this.value,
			hideUngroupedUsers: value
		})
	}

	get showCapacity (): boolean {
		return this.value ? this.value.showCapacity : true
	}
	set showCapacity (value: boolean) {
		this.$emit("input", {
			...this.value,
			showCapacity: value
		})
	}

	get includeUsersWithoutTimeManagement (): boolean {
		return this.value ? this.value.includeUsersWithoutTM : false
	}
	set includeUsersWithoutTimeManagement (value: boolean) {
		this.clearUsers()
		this.$emit("input", {
			...this.value,
			includeUsersWithoutTM: value
		})
	}

	get defaultRange (): DefaultRangeOptions {
		return this.value?.defaultRange ?? DefaultRangeOptions.Next3Weeks
	}
	set defaultRange (value: DefaultRangeOptions) {
		this.$emit("input", {
			...this.value,
			defaultRange: value
		})
	}

	get teamFilter (): string[] {
		return this.value && this.value.teamFilter ? this.value.teamFilter : []
	}
	set teamFilter (value: string[]) {
		this.$emit("input", {
			...this.value,
			teamFilter: value
		})
	}

	get hideDisabledUserAccounts (): boolean {
		return !!this.value?.hideDisabledUserAccounts &&
			this.getIsFeatureEnabled(TenantFeatures.TimeCalendarDisabledUserFormattingAndFiltering)
	}
	set hideDisabledUserAccounts (value: boolean) {
		this.$emit("input", {
			...this.value,
			hideDisabledUserAccounts: value
		})
	}

	private clearUsers () {
		Vue.delete(this, "users")
		this.users = null
	}

	/**
	 * Edit mode filters.
	 * Filters list of users based on Team filter (if any)
	 * @returns {UserWithFields[]} User list
	 */
	get staticallyFilteredUsers (): UserWithFields[] {
		if (!this.users) return []

		let filtered = this.users

		if (this.hideDisabledUserAccounts) {
			filtered = filtered.filter(u => !u.disabled)
		}

		var filterHash = new Set<string>(this.teamFilter)

		if (this.teamFilter.length > 0) {
			filtered = filtered.filter(u => u.teams?.some(t => filterHash.has(t.id)))
		}

		return filtered
	}

	/**
	 * Dynamic filters.
	 * Filters list of users based on User Definition Fields filter (if any)
	 * @returns {UserWithFields[]} User list
	 */
	get dynamicallyFilteredUsers (): UserWithFields[] {
		let filtered = this.staticallyFilteredUsers

		Object.entries(this.userDefinitionFieldFilters).forEach(([definitionFieldId, filter]) => {
			if (filter.enabled && filter.values.length > 0) {
				filtered = filtered.filter(user => {
					const userFieldValue = user.fieldValues && user.fieldValues[definitionFieldId]
					if (!userFieldValue) return false

					if (Array.isArray(userFieldValue.value)) {
						return userFieldValue.value.some((v: IdLabelPair) => filter.values.some(f => f.id === v.id))
					} else {
						return filter.values.some(f => f.id === userFieldValue.value)
					}
				})
			}
		})

		return filtered
	}

	/**
	 * Check that edit feature is enabled and the current user has permission
	 * @returns {boolean} Able to edit
	 */
	get editEnabled (): boolean {
		if (!this.getIsFeatureEnabled(TenantFeatures.TimeManagementDashTileEdit)) return false
		return !!this.currentUser && !!this.currentUser.extendedPermissions && this.currentUser.extendedPermissions[ExtendedPermissions.EditUserSchedules]
	}

	public async mounted () {
		await this.loadUserFieldsIfUnloaded()
		this.loadFileUploadOptions()

		this.userDefinitionFieldFilters = this.tmFilterableUserDefinitionFields
			.toDictionaryMapValue((f: Field) => f.id!, f => ({
				enabled: false,
				values: []
			}))

		// ensure that tenant features and settings have loaded
		if (this.getIsUserInitComplete) this.setDefaultParamsAndSearchIfNeeded()
		else this.awaitingUserInit = true

		const patterns = await api.shiftPatterns()

		this.shiftTitles = patterns.map(p => p.title)
		this.shiftColors = patterns.reduce((acc, p) => {
			if (p.title && p.color) acc[p.title] = p.color
			return acc
		}, {} as Record<string, string>)
	}

	private get defaultSearchRange (): { start: Date, end: Date } {
		const now = DateTime.local()
		const todayUtc = DateTime.utc(now.year, now.month, now.day)

		const searchRange = {
			start: todayUtc.toJSDate(),
			end: null as Date | null
		}

		if (this.defaultRange === DefaultRangeOptions.Next4Weeks) {
			searchRange.end = getEndDateOfWeek(todayUtc.plus({ weeks: 4 }), this.firstDayOfWeek).toJSDate()
		} else if (this.defaultRange === DefaultRangeOptions.Next3Weeks) {
			searchRange.end = getEndDateOfWeek(todayUtc.plus({ weeks: 3 }), this.firstDayOfWeek).toJSDate()
		} else {
			searchRange.end = todayUtc.endOf("month").toJSDate()
		}

		return searchRange as { start: Date, end: Date }
	}

	private resetToDefaultSearchRange (refetch: boolean = true) {
		this.searchRange = this.defaultSearchRange
		this.searchRangeChanged(this.searchRange, refetch)
	}

	private get searchRangeIsDefault (): boolean {
		return this.searchRange.start.getTime() === this.defaultSearchRange.start.getTime() &&
			this.searchRange.end.getTime() === this.defaultSearchRange.end.getTime()
	}

	@Emit("input")
	private teamChanged (groupBy: string) {
		return { ...this.value, groupBy }
	}

	@Watch("edit")
	private async onEditChanged () {
		if (!this.edit) {
			this.doSearch()
		}
	}

	@Watch("getIsUserInitComplete")
	private onUserInitComplete (newValue: boolean) {
		if (newValue && this.awaitingUserInit) {
			this.awaitingUserInit = false
			this.setDefaultParamsAndSearchIfNeeded()
		}
	}

	/**
	 * Fetches users (if not alrady set), then gets the time allocations within the date range and groups them for display
	 */
	async doSearch (forceReloadUsers: boolean = false) {
		this.loadingCases = true
		this.errorLoading = false

		try {
			if (!this.users || forceReloadUsers) {
				Vue.set(this, "users", null)
				this.users = this.includeUsersWithoutTimeManagement
					? await api.usersGet()
					: await api.usersGetWithTimeManagement()
			}

			const start = DateTime.fromJSDate(this.searchRange.start).startOf("day").toISO()
			const end = DateTime.fromJSDate(this.searchRange.end).endOf("day").toISO()
			const userTime = await api.userTimeInRange(start, end)

			const timeByUser = userTime.groupBy("userId")

			const groupedValues: { [key: string]: GroupedUserSchedules } = {}
			this.staticallyFilteredUsers.forEach(user => {
				const userDays = timeByUser[user.id]
				const groupBy = user?.fieldValues && this.groupBy
					? user?.fieldValues[this.groupBy] || null
					: null

				const headerValues: { [key: string]: LabeledValueWithFieldInfo } = {}
				if (user.fieldValues) {
					this.userFields.forEach(userField => {
						if (user.fieldValues![userField.id!]) {
							headerValues[userField.id!] = {
								...user.fieldValues![userField.id!],
								dataType: userField.dataType!,
								fieldTitle: userField.title
							}
						}
					})
				}

				if (this.groupBy) {
					// Remove grouped value from headers - this means we are not duplicating info
					delete headerValues[this.groupBy]
				}

				const userTime = {
					userId: user.id,
					name: user.name,
					userFieldInfo: headerValues,
					assignmentDays: userDays ? userDays.toDictionaryMap(d => DateTime.fromISO(d.date, { zone: "UTC" }).toISODate()) : {},
					busy: false
				}

				if (groupBy) {
					if (Array.isArray(groupBy.value)) {
						// For arrays, add to all items in array
						groupBy.value.forEach(g => {
							if (!groupedValues[g.id]) {
								// Create new group - use id and label
								groupedValues[g.id] = {
									key: g.id,
									label: g.label,
									userSchedules: [],
									totalsRows: []
								}
							}

							groupedValues[g.id].userSchedules.push(userTime)
						})
					} else {
						if (!groupedValues[groupBy.value]) {
							// Create new group - use value and label
							groupedValues[groupBy.value] = {
								key: groupBy.value,
								label: groupBy.label,
								userSchedules: [],
								totalsRows: []
							}
						}

						groupedValues[groupBy.value].userSchedules.push(userTime)
					}
				} else if (!this.hideUngroupedUsers) {
					if (!groupedValues[""]) {
						groupedValues[""] = {
							key: "",
							label: "",
							userSchedules: [],
							totalsRows: []
						}
					}

					// Add to empty group
					groupedValues[""].userSchedules.push(userTime)
				}
			})

			Object.values(groupedValues).forEach(group => {
				group.totalsRows = this.totalsRows(group.key, group.userSchedules)
			})

			this.groupedUserSchedules = Object.values(groupedValues).sortBy("key", false, true)
		} catch (err) {
			this.errorLoading = true
			console.error(err)
		}

		this.loadingCases = false
	}

	searchRangeChanged (newDateRange: { start: Date, end: Date }, refetch: boolean = true) {
		this.searchRange = newDateRange
		if (refetch) this.doSearch()
	}

	async setCapacity ({ group, userId, date, isoFullDate, capacity }:
		{ group: string, userId: string, date: string, isoFullDate: string, capacity: number | string }) {
		const scheduleGroup = this.groupedUserSchedules.find(g => g.key === group)
		if (!scheduleGroup) return

		// Set row as busy
		const userRow = scheduleGroup.userSchedules.find(u => u.userId === userId)
		if (userRow) {
			if (userRow.assignmentDays[date]) {
				Vue.set(userRow.assignmentDays[date], "busy", true)
			} else {
				Vue.set(userRow.assignmentDays, date, { busy: true })
			}
		}

		// Set value
		try {
			const updatedValue = await api.setUserTimeForDate(userId, isoFullDate, capacity)
			if (userRow) {
				// Replace existing value - this will also clear the busy state
				Vue.set(userRow.assignmentDays, date, updatedValue)
			}

			this.updateTotalsValues(scheduleGroup, date)
		} catch (e) {
			console.error(e)
			if (userRow) {
				// On error, clear busy state
				if (userRow.assignmentDays[date]) {
					Vue.set(userRow.assignmentDays[date], "busy", false)
				} else {
					Vue.set(userRow.assignmentDays, date, { busy: false })
				}
			}

			throw e
		}
	}

	totalsRows (group: string, schedules: UserSchedule[]): TotalsRow[] {
		const totalUsers: TotalsRow = {
			type: TotalType.TotalUsers,
			key: group + "-totalUsers",
			title: "Total User Count",
			icon: "fa-solid fa-user",
			values: {}
		}

		const totalScheduledHours: TotalsRow = {
			type: TotalType.TotalScheduledHours,
			key: group + "-totalScheduledHours",
			title: "Total Scheduled Hours",
			icon: "fa-sharp fa-light fa-calendar-clock",
			values: {}
		}

		const forecastedHeadCount: TotalsRow = {
			type: TotalType.RequiredUsers,
			key: group + "-requiredUsers",
			title: "Forecasted Head Count",
			icon: "fa-solid fa-user-clock",
			values: {}
		}

		const productAssignedHours: TotalsRow = {
			type: TotalType.TotalHours,
			key: group + "-totalHours",
			title: "Product Assigned Hours",
			icon: "fa-regular fa-clock",
			values: {}
		}

		this.setTotalRowValues(schedules,
			totalUsers,
			totalScheduledHours,
			forecastedHeadCount,
			productAssignedHours
		)

		return [
			totalUsers,
			totalScheduledHours,
			forecastedHeadCount,
			productAssignedHours
		]
	}

	setTotalRowValues (schedules: UserSchedule[], totalUsers: TotalsRow, totalScheduledHours: TotalsRow,
		forecastedHeadCount: TotalsRow, productAssignedHours: TotalsRow) {
		this.datesInRange.forEach(d => {
			this.setTotalRowValuesForDate(schedules, d.toISODate(),
				totalUsers, totalScheduledHours,
				forecastedHeadCount, productAssignedHours)
		})
	}

	updateTotalsValues (group: GroupedUserSchedules, date: string) {
		const totalsByType = group.totalsRows.toDictionary("type")

		this.setTotalRowValuesForDate(group.userSchedules, date,
			totalsByType[TotalType.TotalUsers],
			totalsByType[TotalType.TotalScheduledHours],
			totalsByType[TotalType.RequiredUsers],
			totalsByType[TotalType.TotalHours])
	}

	/**
	 * @summary Calculates and sets total row values for a specific date.
	 *
	 * Processes user schedules for a given date and updates the total rows values for:
	 * - Total user count
	 * - Total scheduled hours (rounded to 2dp)
	 * - Forecasted head count (rounded to 2dp)
	 * - Product assigned hours (rounded to 2dp)
	 *
	 * Also determines if there is a warning for product assigned hours exceeding scheduled hours.
	 *
	 * @param {UserSchedule[]} schedules - List of user schedules.
	 * @param {string} date - The date for which totals are calculated.
	 * @param {TotalsRow} totalUsers - Tracks the number of users scheduled for the date.
	 * @param {TotalsRow} totalScheduledHours - Tracks the total hours scheduled for all users on the date.
	 * @param {TotalsRow} forecastedHeadCount - Calculates the required head count based on product assigned hours.
	 * @param {TotalsRow} productAssignedHours - Tracks the total hours assigned to products for all users on the date.
	 */
	setTotalRowValuesForDate (
		schedules: UserSchedule[],
		date: string,
		totalUsers: TotalsRow,
		totalScheduledHours: TotalsRow,
		forecastedHeadCount: TotalsRow,
		productAssignedHours: TotalsRow
	) {
		// Initialize values for date
		totalUsers.values[date] = { value: 0 }
		totalScheduledHours.values[date] = { value: 0 }
		productAssignedHours.values[date] = { value: 0, warning: false }

		// For each schedule on date, set total values
		schedules.forEach(s => {
			const assignment = s.assignmentDays[date]
			if (assignment) {
				if (assignment.capacityTotal) {
					totalUsers.values[date].value++
					totalScheduledHours.values[date].value += assignment.capacityTotal
				}

				if (assignment.assignments) {
					const totalAssignedHours = assignment.assignments.reduce((total, assignment) => {
						const roundedHours = Math.round(assignment.hours * 100) / 100
						return total + roundedHours
					}, 0)

					productAssignedHours.values[date].value += totalAssignedHours
				}
			}
		})

		totalScheduledHours.values[date].value = Math.round(totalScheduledHours.values[date].value * 100) / 100
		productAssignedHours.values[date].value = Math.round(productAssignedHours.values[date].value * 100) / 100

		const forecastedValue = productAssignedHours.values[date].value / this.hoursPerDay
		forecastedHeadCount.values[date] = {
			value: Math.round(forecastedValue * 100) / 100 || 0
		}

		if (totalScheduledHours.values[date] !== undefined &&
				productAssignedHours.values[date] !== undefined) {
			productAssignedHours.values[date].warning =
					productAssignedHours.values[date].value > totalScheduledHours.values[date].value
		}
	}

	/**
	 * @summary Sets the default parameters for the component and searches if not in edit mode.
	 * Called on load when tenant features and settings have loaded in.
	 */
	private setDefaultParamsAndSearchIfNeeded () {
		this.resetToDefaultSearchRange(false)
		this.onEditChanged()
	}

	private get tmFilterableUserDefinitionFields (): Field[] {
		const filterableUserFieldDataTypes = ["list", "list-multiple"]
		return this.userFields.filter(uf => !!uf.dataType && filterableUserFieldDataTypes.includes(uf.dataType))
	}

	private isDefinitionFieldFilterEnabled (definitionFieldId: string | undefined) {
		if (!definitionFieldId) return false
		return this.userDefinitionFieldFilters[definitionFieldId]?.enabled ?? false
	}

	private getDefinitionFieldFilter (definitionFieldId: string | undefined) {
		if (!definitionFieldId || !this.userDefinitionFieldFilters) return null
		return this.userDefinitionFieldFilters[definitionFieldId] ?? null
	}

	private get filteredUserScheduleGroups (): GroupedUserSchedules[] {
		const filteredGroups = cloneDeep(this.groupedUserSchedules)

		filteredGroups.forEach(uScheduleGroup => {
			uScheduleGroup.userSchedules = uScheduleGroup.userSchedules.filter((uSchedule: UserSchedule) => {
				return this.dynamicallyFilteredUsers.some(u => u.id === uSchedule.userId)
			})
			uScheduleGroup.totalsRows = this.totalsRows(uScheduleGroup.key, uScheduleGroup.userSchedules)
		})

		filteredGroups.removeWhere(fg => fg.userSchedules.length === 0)

		return filteredGroups
	}

	private set filteredUserScheduleGroups (newValue: GroupedUserSchedules[]) {
		if (newValue.length < this.groupedUserSchedules.length) {
			newValue.forEach(g => {
				const existing = this.groupedUserSchedules.find(gs => gs.key === g.key)
				if (existing) {
					existing.userSchedules = g.userSchedules
					existing.totalsRows = g.totalsRows
				}
			})
		} else {
			this.groupedUserSchedules = newValue
		}
	}

	private searchRangeShiftStep (direction: TimeShiftDirection) {
		this.loadingCases = true
		this.errorLoading = false
		this.groupedUserSchedules = []

		const stepValue = direction === TimeShiftDirection.Backwards ? -1 : 1

		const currentStart = DateTime.fromJSDate(this.searchRange.start, { zone: "utc" })
		const currentEnd = DateTime.fromJSDate(this.searchRange.end, { zone: "utc" })

		const newRange = {
			start: currentStart.plus({ weeks: stepValue }).toJSDate(),
			end: currentEnd.plus({ weeks: stepValue }).toJSDate()
		}

		this.searchRangeChanged(newRange)
	}

	private get activeUserDefinitionFieldFilterCount (): number {
		return Object.values(this.userDefinitionFieldFilters).filter(f => f.enabled && Object.keys(f.values).length > 0).length
	}

	private get filtersTooltipText (): string {
		const topText = `${(this.showingFilters ? "Hide filters" : "Show filters")} (${this.activeUserDefinitionFieldFilterCount} active)`
		let subText = ""

		Object.entries(this.userDefinitionFieldFilters).filter((value, index) => value[1].enabled && value[1].values.length > 0)
			.forEach(([definitionFieldId, filter]) => {
				const definitionField = this.tmFilterableUserDefinitionFields.find(f => f.id === definitionFieldId)
				if (definitionField) {
					subText += `\n\n<strong>${definitionField.title}:</strong>\n<em>${filter.values.map(v => v.label).join(",\n")}</em>`
				}
			})

		return subText ? `${topText}:${subText}` : topText
	}

	get isFreezeScheduleManagementFilterOptionsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.FreezeScheduleManagementFilterOptions)
	}
}
